import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import type { VehicleData, AppStep, VehicleReport, SessionVehicle, CatalogResponse, QuoteResponse } from '@/types';
import {
  getSessionState,
  getSessionVehicles,
  fetchCatalog,
  fetchQuote,
  selectVehiclesAndPackage,
  createPaymentIntent,
  verifyPayment,
  initiateTeslaOAuth,
  generateReports,
} from '@/services/api';
import type { VehicleReportOutcome } from '@/services/api';

/** Raw response shape returned by the generate-reports endpoint. */
type GenerateReportsResult = Awaited<ReturnType<typeof generateReports>>;

/** Parsed result of a report-generation run, returned by the store action. */
export interface GenerateReportsOutcome {
  /** The raw server response (status, retry_count, error, ...). */
  result: GenerateReportsResult;
  /** Reports that carry usable vehicle data, keyed by VIN. */
  typedReports: Record<string, VehicleReport>;
  /** Per-vehicle error messages for reports that failed to materialise. */
  failedReports: string[];
}

// localStorage keys
const STORAGE_KEYS = {
  paymentId: 'cpt-payment-id',
  selectedPlan: 'cpt-selected-plan',
  paymentMethod: 'cpt-payment-method',
  currentStep: 'cpt-current-step',
  selectedBrandId: 'cpt-selected-brand-id',
  sessionId: 'cpt-session-id',
  sessionExpiresAt: 'cpt-session-expires-at',
  sessionVehicles: 'cpt-session-vehicles',
  vehicleReports: 'cpt-vehicle-reports',
  vehicleReportOutcomes: 'cpt-vehicle-report-outcomes',
  activeReportVin: 'cpt-active-report-vin',
  vehiclePhotos: 'cpt-vehicle-photos',
} as const;

// Steps that are no longer valid (removed in v2) — reset to landing
const LEGACY_STEPS = new Set(['payment-success', 'verify', 'payment', 'oauth-loading']);

function loadFromStorage<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error(`[Store] Failed to load ${key} from localStorage:`, error);
    return defaultValue;
  }
}

function saveToStorage(key: string, value: any): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  } catch (error) {
    console.error(`[Store] Failed to save ${key} to localStorage:`, error);
  }
}

export const useAppStore = defineStore('app', () => {
  // State - load from localStorage if available
  const storedStep = loadFromStorage<string>(STORAGE_KEYS.currentStep, 'landing');
  const isOAuthReturn = new URLSearchParams(window.location.search).get('oauth') === 'success';
  const currentStep = ref<AppStep>(
    isOAuthReturn ? 'oauth-loading'
    : (storedStep === 'support' || LEGACY_STEPS.has(storedStep) ? 'landing' : storedStep) as AppStep
  );
  const previousStep = ref<AppStep | null>(null);
  const vehicleData = ref<VehicleData | null>(null);
  const vehicleReport = ref<VehicleReport | null>(null);
  const isProcessing = ref(false);
  const error = ref<string | null>(null);
  const paymentId = ref<string | null>(loadFromStorage(STORAGE_KEYS.paymentId, null));
  const selectedPlan = ref<string | null>(loadFromStorage(STORAGE_KEYS.selectedPlan, null));
  const paymentMethod = ref<'stripe' | 'googlepay' | 'applepay' | null>(loadFromStorage(STORAGE_KEYS.paymentMethod, null));
  // Tesla is the only active brand — pre-select it so the user lands ready to
  // connect without an extra click. A previously stored value still wins.
  const selectedBrandId = ref<string | null>(loadFromStorage(STORAGE_KEYS.selectedBrandId, 'tesla'));
  const stripeClientSecret = ref<string | null>(null);

  /** OrderSession ID — persisted to survive OAuth/payment redirects */
  const sessionId = ref<string | null>(
    (typeof window !== 'undefined' && sessionStorage.getItem(STORAGE_KEYS.sessionId)
      ? JSON.parse(sessionStorage.getItem(STORAGE_KEYS.sessionId)!)
      : null) || loadFromStorage(STORAGE_KEYS.sessionId, null)
  );
  const sessionExpiresAt = ref<string | null>(loadFromStorage(STORAGE_KEYS.sessionExpiresAt, null));

  /** Vehicles from session — persisted to survive page refresh */
  const sessionVehicles = ref<SessionVehicle[]>(loadFromStorage(STORAGE_KEYS.sessionVehicles, []));

  /** CPQ pricing: catalog and current quote */
  const catalog = ref<CatalogResponse | null>(null);
  const currentQuote = ref<QuoteResponse | null>(null);

  /** Loading flags for shared async API operations (debuggable via DevTools) */
  const catalogLoading = ref(false);
  const quoteLoading = ref(false);
  const reportsLoading = ref(false);

  /** Multi-report: all vehicle reports keyed by VIN — persisted to survive refresh */
  const vehicleReports = ref<Record<string, VehicleReport>>(loadFromStorage(STORAGE_KEYS.vehicleReports, {}));
  const vehicleReportOutcomes = ref<Record<string, VehicleReportOutcome>>(loadFromStorage(STORAGE_KEYS.vehicleReportOutcomes, {}));
  const activeReportVin = ref<string | null>(loadFromStorage(STORAGE_KEYS.activeReportVin, null));

  /** Vehicle photos: base64-encoded images keyed by VIN — persisted to localStorage */
  const vehiclePhotos = ref<Record<string, string[]>>(loadFromStorage(STORAGE_KEYS.vehiclePhotos, {}));

  // Getters
  const hasVehicleData = computed(() => vehicleData.value !== null);
  const hasReport = computed(() => vehicleReport.value !== null);

  /** True when sessionExpiresAt is set and still in the future. Single source
   *  of truth for "the session window is valid" — consumed by the step
   *  indicator's clickability logic so the expiry rule lives in one place. */
  const hasValidSession = computed(() => {
    if (!sessionExpiresAt.value) return false;
    return new Date(sessionExpiresAt.value).getTime() > Date.now();
  });

  /** True when a valid, non-expired session exists (Tesla OAuth completed) */
  const isAuthenticated = computed(() => !!sessionId.value && hasValidSession.value);

  /** Alias for UI locking logic — tabs become non-clickable when frozen */
  const isFrozen = computed(() => isAuthenticated.value);

  // Watch for changes and persist to localStorage
  watch(paymentId, (v) => saveToStorage(STORAGE_KEYS.paymentId, v), { immediate: false });
  watch(selectedPlan, (v) => saveToStorage(STORAGE_KEYS.selectedPlan, v), { immediate: false });
  watch(paymentMethod, (v) => saveToStorage(STORAGE_KEYS.paymentMethod, v), { immediate: false });
  watch(currentStep, (v) => {
    if (v !== 'support') saveToStorage(STORAGE_KEYS.currentStep, v);
  }, { immediate: false });
  watch(selectedBrandId, (v) => saveToStorage(STORAGE_KEYS.selectedBrandId, v), { immediate: false });
  watch(sessionId, (v) => {
    saveToStorage(STORAGE_KEYS.sessionId, v);
    // Also save to sessionStorage so session ID doesn't survive browser close
    if (typeof window !== 'undefined') {
      if (v) sessionStorage.setItem(STORAGE_KEYS.sessionId, JSON.stringify(v));
      else sessionStorage.removeItem(STORAGE_KEYS.sessionId);
    }
  }, { immediate: false });
  watch(sessionExpiresAt, (v) => saveToStorage(STORAGE_KEYS.sessionExpiresAt, v), { immediate: false });
  watch(vehicleReports, (v) => saveToStorage(STORAGE_KEYS.vehicleReports, v), { immediate: false, deep: true });
  watch(vehicleReportOutcomes, (v) => saveToStorage(STORAGE_KEYS.vehicleReportOutcomes, v), { immediate: false, deep: true });
  watch(activeReportVin, (v) => saveToStorage(STORAGE_KEYS.activeReportVin, v), { immediate: false });
  watch(vehiclePhotos, (v) => saveToStorage(STORAGE_KEYS.vehiclePhotos, v), { immediate: false, deep: true });

  // Actions
  function setStep(step: AppStep) {
    currentStep.value = step;
    window.scrollTo(0, 0);
  }

  function setVehicleData(data: VehicleData) {
    vehicleData.value = data;
  }

  function setVehicleReport(report: VehicleReport) {
    vehicleReport.value = report;
    if (report.vehicle) {
      vehicleData.value = report.vehicle;
    }
  }

  function setProcessing(processing: boolean) {
    isProcessing.value = processing;
  }

  function setError(errorMessage: string | null) {
    error.value = errorMessage;
  }

  function setPaymentId(id: string) {
    paymentId.value = id;
    saveToStorage(STORAGE_KEYS.paymentId, id);
  }

  function setSelectedPlan(plan: string) {
    selectedPlan.value = plan;
    saveToStorage(STORAGE_KEYS.selectedPlan, plan);
  }

  function setPaymentMethod(method: 'stripe' | 'googlepay' | 'applepay') {
    paymentMethod.value = method;
    saveToStorage(STORAGE_KEYS.paymentMethod, method);
  }

  function setStripeClientSecret(secret: string | null) {
    stripeClientSecret.value = secret;
  }

  function setSessionId(id: string | null) {
    sessionId.value = id;
    saveToStorage(STORAGE_KEYS.sessionId, id);
  }

  function setSessionExpiresAt(value: string | null) {
    sessionExpiresAt.value = value;
    saveToStorage(STORAGE_KEYS.sessionExpiresAt, value);
  }

  function setSessionVehicles(vehicles: SessionVehicle[]) {
    sessionVehicles.value = vehicles;
    saveToStorage(STORAGE_KEYS.sessionVehicles, vehicles);
  }

  function setSelectedBrandId(brandId: string | null) {
    selectedBrandId.value = brandId;
    saveToStorage(STORAGE_KEYS.selectedBrandId, brandId);
  }

  function setCatalog(data: CatalogResponse | null) {
    catalog.value = data;
  }

  function setCurrentQuote(quote: QuoteResponse | null) {
    currentQuote.value = quote;
  }

  function setVehicleReports(reports: Record<string, VehicleReport>) {
    vehicleReports.value = reports;
    const vins = Object.keys(reports);
    if (vins.length > 0 && !activeReportVin.value) {
      activeReportVin.value = vins[0];
    }
  }

  function setVehicleReportOutcomes(outcomes: Record<string, VehicleReportOutcome>) {
    vehicleReportOutcomes.value = outcomes;
  }

  function setActiveReportVin(vin: string | null) {
    activeReportVin.value = vin;
  }

  function addVehiclePhoto(vin: string, base64DataUrl: string) {
    const current = vehiclePhotos.value[vin] || [];
    if (current.length >= 10) return;
    vehiclePhotos.value = { ...vehiclePhotos.value, [vin]: [...current, base64DataUrl] };
  }

  function removeVehiclePhoto(vin: string, index: number) {
    const current = vehiclePhotos.value[vin] || [];
    const updated = current.filter((_, i) => i !== index);
    vehiclePhotos.value = { ...vehiclePhotos.value, [vin]: updated };
  }

  function getVehiclePhotos(vin: string): string[] {
    return vehiclePhotos.value[vin] || [];
  }

  function navigateToSupport() {
    previousStep.value = currentStep.value;
    currentStep.value = 'support';
    window.scrollTo(0, 0);
  }

  function returnFromSupport() {
    currentStep.value = previousStep.value || 'landing';
    previousStep.value = null;
    window.scrollTo(0, 0);
  }

  /** Attempt to resume session from backend (PostgreSQL). Returns true if resumed. */
  async function resumeSession(): Promise<boolean> {
    const sid = sessionId.value;
    if (!sid) return false;
    try {
      const state = await getSessionState(sid);
      // Hydrate store from server state
      sessionVehicles.value = state.vehicles;
      saveToStorage(STORAGE_KEYS.sessionVehicles, state.vehicles);
      sessionExpiresAt.value = state.expires_at || null;
      saveToStorage(STORAGE_KEYS.sessionExpiresAt, state.expires_at || null);
      if (state.selected_package) {
        selectedPlan.value = state.selected_package;
        saveToStorage(STORAGE_KEYS.selectedPlan, state.selected_package);
      }
      if (state.payment_id) {
        paymentId.value = state.payment_id;
        saveToStorage(STORAGE_KEYS.paymentId, state.payment_id);
      }
      if (state.payment_method) {
        paymentMethod.value = state.payment_method as any;
        saveToStorage(STORAGE_KEYS.paymentMethod, state.payment_method);
      }
      if (state.reports && Object.keys(state.reports).length > 0) {
        vehicleReports.value = state.reports;
        saveToStorage(STORAGE_KEYS.vehicleReports, state.reports);
        const vins = Object.keys(state.reports);
        if (vins.length > 0 && !activeReportVin.value) {
          activeReportVin.value = vins[0];
          saveToStorage(STORAGE_KEYS.activeReportVin, vins[0]);
        }
      }
      if (state.outcomes && Object.keys(state.outcomes).length > 0) {
        vehicleReportOutcomes.value = state.outcomes;
        saveToStorage(STORAGE_KEYS.vehicleReportOutcomes, state.outcomes);
      }
      // Set the step from server state
      const serverStep = state.step as AppStep;
      if (serverStep && serverStep !== 'landing') {
        currentStep.value = serverStep;
        saveToStorage(STORAGE_KEYS.currentStep, serverStep);
      }
      return true;
    } catch (err) {
      console.warn('[Store] Session resume failed (session may have expired):', err);
      clearExpiredSessionState();
      return false;
    }
  }

  function clearExpiredSessionState() {
    sessionId.value = null;
    sessionExpiresAt.value = null;
    sessionVehicles.value = [];
    vehicleReports.value = {};
    activeReportVin.value = null;
    vehicleReport.value = null;
    vehicleData.value = null;
    currentQuote.value = null;
    paymentId.value = null;
    selectedPlan.value = null;
    paymentMethod.value = null;
    stripeClientSecret.value = null;
    vehiclePhotos.value = {};
    saveToStorage(STORAGE_KEYS.sessionId, null);
    saveToStorage(STORAGE_KEYS.sessionExpiresAt, null);
    saveToStorage(STORAGE_KEYS.sessionVehicles, null);
    saveToStorage(STORAGE_KEYS.vehicleReports, null);
    saveToStorage(STORAGE_KEYS.activeReportVin, null);
    saveToStorage(STORAGE_KEYS.paymentId, null);
    saveToStorage(STORAGE_KEYS.selectedPlan, null);
    saveToStorage(STORAGE_KEYS.paymentMethod, null);
    saveToStorage(STORAGE_KEYS.vehiclePhotos, null);
  }

  // ── Async API actions ────────────────────────────────────────────
  // These wrap the api.ts functions and store their results as reactive
  // state, so views share a single source of truth instead of each calling
  // the backend and holding private copies.

  /** Fetch the CPQ pricing catalog and store it. Returns the catalog, or null on failure. */
  async function loadCatalog(): Promise<CatalogResponse | null> {
    catalogLoading.value = true;
    try {
      const data = await fetchCatalog();
      catalog.value = data;
      return data;
    } catch (err) {
      console.error('[Store] Failed to load catalog:', err);
      return null;
    } finally {
      catalogLoading.value = false;
    }
  }

  /**
   * Select vehicles + package on the session, then fetch and store the quote.
   * Returns the quote, or null when there is nothing to quote or the request fails.
   */
  async function loadQuote(plan: string, vehicleIds: number[]): Promise<QuoteResponse | null> {
    const sid = sessionId.value;
    if (!sid || vehicleIds.length === 0) return null;
    quoteLoading.value = true;
    try {
      await selectVehiclesAndPackage(sid, vehicleIds, plan);
      const q = await fetchQuote(sid);
      currentQuote.value = q;
      return q;
    } catch (err) {
      console.error('[Store] Quote error:', err);
      currentQuote.value = null;
      return null;
    } finally {
      quoteLoading.value = false;
    }
  }

  /**
   * Fetch vehicles for a session after Tesla OAuth and hydrate session state
   * (vehicles, expiry, pre-selected package). Returns true on success.
   */
  async function loadSessionVehicles(id: string): Promise<boolean> {
    setSessionId(id);
    try {
      const session = await getSessionVehicles(id);
      setSessionVehicles(session.vehicles);
      setSessionExpiresAt(session.expiresAt || null);
      if (session.selectedPackage) {
        setSelectedPlan(session.selectedPackage);
      }
      return true;
    } catch (err) {
      console.error('[Store] Failed to fetch session vehicles:', err);
      return false;
    }
  }

  /** Create a payment intent for the active session and store the payment id. */
  async function createPayment(
    method: 'stripe'
  ): Promise<{ clientSecret: string; paymentIntentId: string; redirectUrl?: string }> {
    const sid = sessionId.value;
    if (!sid) throw new Error('Session expired. Please start over.');
    const result = await createPaymentIntent(sid, method);
    setPaymentId(result.paymentIntentId);
    return result;
  }

  /** Verify a payment by id. Returns the status + resolved plan from the backend. */
  function verifyPaymentStatus(id: string): Promise<{ status: string; plan: string }> {
    return verifyPayment(id);
  }

  /** Begin the Tesla OAuth flow for a brand. Returns the provider authorization URL. */
  function startTeslaOAuth(brand: string): Promise<string> {
    return initiateTeslaOAuth(brand);
  }

  /**
   * Generate vehicle reports for the active session, store the typed reports +
   * outcomes, and return the parsed result so callers can drive navigation/retry.
   * Pass { merge: true } to fold new reports into the existing set (per-vehicle retry).
   */
  async function generateVehicleReports(
    lang?: string,
    options: { merge?: boolean } = {}
  ): Promise<GenerateReportsOutcome> {
    const sid = sessionId.value;
    if (!sid) throw new Error('Session data is missing.');
    reportsLoading.value = true;
    try {
      const result = await generateReports(sid, lang);
      const typedReports: Record<string, VehicleReport> = {};
      const failedReports: string[] = [];
      if (result.status === 'complete' && result.reports) {
        for (const key of Object.keys(result.reports)) {
          const candidate = result.reports[key] as VehicleReport & { error?: string };
          if (candidate?.vehicle || candidate?.vehicleData) {
            typedReports[key] = candidate as VehicleReport;
          } else if (candidate?.error) {
            failedReports.push(candidate.error);
          }
        }
        if (Object.keys(typedReports).length > 0) {
          if (options.merge) {
            vehicleReports.value = { ...vehicleReports.value, ...typedReports };
            if (result.outcomes) {
              vehicleReportOutcomes.value = { ...vehicleReportOutcomes.value, ...result.outcomes };
            }
          } else {
            setVehicleReports(typedReports);
            setVehicleReportOutcomes(result.outcomes || {});
          }
        }
      }
      return { result, typedReports, failedReports };
    } finally {
      reportsLoading.value = false;
    }
  }

  function reset() {
    currentStep.value = 'landing';
    vehicleData.value = null;
    vehicleReport.value = null;
    isProcessing.value = false;
    error.value = null;
    paymentId.value = null;
    selectedPlan.value = null;
    paymentMethod.value = null;
    stripeClientSecret.value = null;
    selectedBrandId.value = 'tesla'; // keep Tesla pre-selected after a reset
    sessionId.value = null;
    sessionExpiresAt.value = null;
    sessionVehicles.value = [];
    catalog.value = null;
    currentQuote.value = null;
    vehicleReports.value = {};
    activeReportVin.value = null;
    vehiclePhotos.value = {};
    Object.values(STORAGE_KEYS).forEach((key) => saveToStorage(key, null));
    saveToStorage(STORAGE_KEYS.currentStep, 'landing');
  }

  return {
    currentStep,
    vehicleData,
    vehicleReport,
    isProcessing,
    error,
    paymentId,
    selectedPlan,
    paymentMethod,
    stripeClientSecret,
    sessionId,
    sessionExpiresAt,
    sessionVehicles,
    selectedBrandId,
    catalog,
    currentQuote,
    catalogLoading,
    quoteLoading,
    reportsLoading,
    vehicleReports,
    vehicleReportOutcomes,
    activeReportVin,
    vehiclePhotos,
    hasVehicleData,
    hasReport,
    hasValidSession,
    isAuthenticated,
    isFrozen,
    setStep,
    setVehicleData,
    setVehicleReport,
    setProcessing,
    setError,
    setPaymentId,
    setSelectedPlan,
    setPaymentMethod,
    setStripeClientSecret,
    setSessionId,
    setSessionExpiresAt,
    setSessionVehicles,
    setSelectedBrandId,
    setCatalog,
    setCurrentQuote,
    setVehicleReports,
    setVehicleReportOutcomes,
    setActiveReportVin,
    addVehiclePhoto,
    removeVehiclePhoto,
    getVehiclePhotos,
    navigateToSupport,
    returnFromSupport,
    resumeSession,
    loadCatalog,
    loadQuote,
    loadSessionVehicles,
    createPayment,
    verifyPaymentStatus,
    startTeslaOAuth,
    generateVehicleReports,
    clearExpiredSessionState,
    reset,
  };
});
